/**
 * Responsive Layout Controller
 * Applies data attributes and CSS variables so the dashboard can respond
 * to viewport changes without DOM rewrites.
 */
export class ResponsiveLayoutController {
  constructor(options = {}) {
    this.breakpoints = {
      compact: 0,
      cozy: 640,
      roomy: 960,
      spacious: 1320,
    };

    if (options.breakpoints) {
      this.breakpoints = {
        ...this.breakpoints,
        ...options.breakpoints,
      };
    }

    this.onChange = typeof options.onChange === 'function' ? options.onChange : null;
    this.root = document.documentElement;
    this.body = document.body;
    this.currentTier = null;
    this.currentWidth = window.innerWidth;
    this.resizeFrame = null;
    this.isDestroyed = false;
    this.customProps = {
      gutter: options.gutter ?? { min: 16, max: 48 },
      containerMaxWidth: options.containerMaxWidth ?? 1440,
    };

    this.handleResize = this.handleResize.bind(this);
    this.updateTier = this.updateTier.bind(this);
    this.listenerOptions = { passive: true };

    this.initialize();
  }

  initialize() {
    this.body?.setAttribute('data-layout-ready', 'true');
    this.applyCustomProperties();
    this.updateTier(this.currentWidth);

    window.addEventListener('resize', this.handleResize, this.listenerOptions);
  }

  applyCustomProperties() {
    const { gutter, containerMaxWidth } = this.customProps;
    const gutterRule = `clamp(${gutter.min}px, 4vw, ${gutter.max}px)`;

    this.root.style.setProperty('--layout-gutter', gutterRule);
    this.root.style.setProperty('--layout-container-max', `${containerMaxWidth}px`);
  }

  handleResize() {
    if (this.isDestroyed) return;

    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame);
    }

    this.resizeFrame = requestAnimationFrame(() => {
      this.currentWidth = window.innerWidth;
      this.updateTier(this.currentWidth);
    });
  }

  updateTier(width) {
    if (this.isDestroyed) return;

    const tier = this.computeTier(width);
    if (tier === this.currentTier) {
      return;
    }

    this.currentTier = tier;

    if (this.body) {
      this.body.setAttribute('data-layout-tier', tier);
      this.body.setAttribute('data-layout-width', String(width));
    }

    const detail = {
      tier,
      width,
      breakpoints: { ...this.breakpoints },
    };

    document.dispatchEvent(new CustomEvent('layout:change', { detail }));
    if (this.onChange) {
      this.onChange(detail);
    }
  }

  computeTier(width) {
    if (width >= this.breakpoints.spacious) {
      return 'spacious';
    }
    if (width >= this.breakpoints.roomy) {
      return 'roomy';
    }
    if (width >= this.breakpoints.cozy) {
      return 'cozy';
    }
    return 'compact';
  }

  destroy() {
    this.isDestroyed = true;
    window.removeEventListener('resize', this.handleResize, this.listenerOptions);
    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame);
    }
    this.body?.removeAttribute('data-layout-tier');
    this.body?.removeAttribute('data-layout-width');
    this.body?.removeAttribute('data-layout-ready');
  }
}
